import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { DollarSign, Eye } from 'lucide-react';
import { LecturerSalaryDetailsDialog } from './LecturerSalaryDetailsDialog';
import { useOptimizedLecturerSalary } from '@/hooks/useOptimizedLecturerSalary';

export const LecturerSalarySection = () => {
  const [payingPhase, setPayingPhase] = useState<string | null>(null);
  const [selectedLecturerId, setSelectedLecturerId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Use the optimized hook
  const {
    summaries: lecturerSummaries,
    detailsMap: salaryDetails,
    loading,
    markSalaryAsPaid,
    formatAmount
  } = useOptimizedLecturerSalary();

  const handleMarkSalaryAsPaid = async (phaseId: string, groupId: string) => {
    try {
      const payingKey = `${phaseId}-${groupId}`;
      setPayingPhase(payingKey);

      if (!selectedLecturerId) {
        throw new Error('Ընտրված դասախոս չկա');
      }

      await markSalaryAsPaid(phaseId, groupId, selectedLecturerId);

    } catch (error: any) {
      console.error('❌ Error in handleMarkSalaryAsPaid:', error);
    } finally {
      setPayingPhase(null);
    }
  };

  const openDetailsDialog = (lecturerId: string) => {
    setSelectedLecturerId(lecturerId);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-muted-foreground">
            <DollarSign className="h-5 w-5" />
            Դասախոսների ակնկալվող աշխատավարձներ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-muted-foreground">Բեռնվում է...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-muted-foreground">
          <DollarSign className="h-5 w-5" />
          Դասախոսների ակնկալվող աշխատավարձներ
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {lecturerSummaries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Դասախոսային աշխատավարձներ չկան
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <Table className="min-w-[880px]">
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-muted-foreground">Դասախոս</TableHead>
                  <TableHead className="text-muted-foreground">Փուլերի քանակ</TableHead>
                  <TableHead className="text-muted-foreground">Ընդհանուր ակնկալվող</TableHead>
                  <TableHead className="text-muted-foreground">Վճարված</TableHead>
                  <TableHead className="text-muted-foreground">Վճարելի</TableHead>
                  <TableHead className="text-muted-foreground">Մնացած</TableHead>
                  <TableHead className="text-muted-foreground">Գործողություն</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lecturerSummaries.map((summary) => (
                  <TableRow key={summary.lecturer_id} className="border-border">
                    <TableCell className="text-foreground font-semibold">
                      {summary.lecturer_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono">
                        {summary.phase_count} փուլ
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-primary">
                        {formatAmount(summary.total_expected_salary)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-primary">
                        {formatAmount(summary.total_paid_salary)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-primary">
                        {formatAmount(summary.total_pending_salary)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-primary">
                        {formatAmount(summary.total_expected_salary - summary.total_paid_salary - summary.total_pending_salary)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetailsDialog(summary.lecturer_id)}
                        className="border-border/70 text-muted-foreground hover:bg-primary/10"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Մանրամասներ
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Details Dialog */}
        <LecturerSalaryDetailsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          lecturerName={selectedLecturerId ? lecturerSummaries.find(l => l.lecturer_id === selectedLecturerId)?.lecturer_name || '' : ''}
          salaryDetails={selectedLecturerId ? salaryDetails[selectedLecturerId] || [] : []}
          onMarkAsPaid={handleMarkSalaryAsPaid}
          payingPhase={payingPhase}
        />
      </CardContent>
    </Card>
  );
};